import React, { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Timer, Play, X } from "lucide-react";
import { toast } from "sonner";
import { celebrateLarge } from "../../utils/celebrate";

// A global, always-available Focus Mode control for the whole Learning Forest -
// every subject, lesson, quiz, game and flashcard. Hides the site header (via the
// `iw-focus-mode` body class) for a calm full-screen view, and offers an optional
// gentle countdown timer for a focused practice sprint.
const TIMER_MINUTES = [5, 10, 15];
const SPRINT_KEY = "iw_focus_sprint_v1"; // remaining seconds of an unfinished sprint
const STREAK_KEY = "iw_focus_streak_v1"; // { count, lastDate } daily focus streak

// Bump the daily focus streak when a sprint is COMPLETED. Same-day repeats keep
// the count; a next-day finish grows it; a gap resets to 1. Returns new count.
function bumpFocusStreak() {
  const today = new Date().toISOString().slice(0, 10);
  let data = { count: 0, lastDate: "" };
  try { data = JSON.parse(localStorage.getItem(STREAK_KEY) || "{}") || {}; } catch { data = {}; }
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  let count = data.count || 0;
  if (data.lastDate === today) { /* already counted today */ }
  else if (data.lastDate === y) count += 1;
  else count = 1;
  try { localStorage.setItem(STREAK_KEY, JSON.stringify({ count, lastDate: today })); } catch { /* ignore */ }
  return count;
}
function readFocusStreak() {
  try {
    const d = JSON.parse(localStorage.getItem(STREAK_KEY) || "{}") || {};
    return d.count || 0;
  } catch { return 0; }
}

export default function FocusModeButton() {
  const [on, setOn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [savedSprint, setSavedSprint] = useState(0); // resumable leftover secs
  const [streak, setStreak] = useState(readFocusStreak());
  const [progress, setProgress] = useState(null); // { current, total }
  const intervalRef = useRef(null);

  const clearTimer = () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };

  // Persist any unfinished sprint on unmount so it survives navigation/refresh.
  useEffect(() => () => {
    document.body.classList.remove("iw-focus-mode");
    clearTimer();
  }, []);

  // Stay in sync when the per-question Focus button (in QuestionReader) toggles
  // focus, and track the live quiz progress so we can show a "3 of 5" ring.
  useEffect(() => {
    const syncFocus = (e) => setOn(!!e.detail?.on);
    const syncProgress = (e) => {
      const d = e.detail || {};
      if (typeof d.total === "number" && d.total > 0 && typeof d.current === "number") {
        setProgress({ current: d.current, total: d.total });
      }
    };
    window.addEventListener("iw-focus-changed", syncFocus);
    window.addEventListener("iw-quiz-progress", syncProgress);
    return () => {
      window.removeEventListener("iw-focus-changed", syncFocus);
      window.removeEventListener("iw-quiz-progress", syncProgress);
    };
  }, []);

  const persistSprint = (secs) => {
    try {
      if (secs > 0) localStorage.setItem(SPRINT_KEY, String(secs));
      else localStorage.removeItem(SPRINT_KEY);
    } catch { /* ignore */ }
  };

  const toggle = () => {
    const next = !on;
    setOn(next);
    document.body.classList.toggle("iw-focus-mode", next);
    if (!next) {
      // Leaving focus mid-sprint: park the remaining time to auto-resume later.
      clearTimer();
      persistSprint(secondsLeft);
      setSecondsLeft(0);
    } else {
      // Entering focus: surface any parked sprint so the timer resumes where
      // it left off instead of starting over.
      let saved = 0;
      try { saved = parseInt(localStorage.getItem(SPRINT_KEY) || "0", 10) || 0; } catch { saved = 0; }
      setSavedSprint(saved > 0 ? saved : 0);
    }
    try { window.dispatchEvent(new CustomEvent("iw-focus-changed", { detail: { on: next } })); } catch { /* noop */ }
  };

  const runCountdown = (startSecs, mins) => {
    clearTimer();
    setSecondsLeft(startSecs);
    persistSprint(startSecs);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearTimer();
          persistSprint(0);
          const newStreak = bumpFocusStreak();
          setStreak(newStreak);
          celebrateLarge();
          toast(`⏱️ Nice focus! Your ${mins}-minute sprint is done. 🔥 ${newStreak}-day focus streak!`);
          return 0;
        }
        persistSprint(s - 1);
        return s - 1;
      });
    }, 1000);
  };

  const startTimer = (mins) => { setSavedSprint(0); runCountdown(mins * 60, mins); };
  const resumeTimer = () => {
    if (savedSprint <= 0) return;
    const secs = savedSprint;
    setSavedSprint(0);
    runCountdown(secs, Math.max(1, Math.round(secs / 60)));
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!on) {
    return (
      <button
        type="button"
        onClick={toggle}
        data-testid="focus-mode-btn"
        title="Focus mode, hide the clutter"
        className="fixed bottom-5 right-5 z-[95] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-indigo-600/90 hover:bg-indigo-500 text-white text-xs font-bold shadow-lg border border-indigo-300/30 backdrop-blur"
      >
        <Maximize2 size={14} /> Focus
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[95] flex items-center gap-2 rounded-full bg-slate-900/90 border border-white/15 shadow-xl backdrop-blur px-2 py-1.5" data-testid="focus-mode-bar">
      {progress && progress.total > 0 && (
        <QuizProgressRing current={progress.current} total={progress.total} />
      )}
      {streak > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-200 text-[11px] font-bold" data-testid="focus-streak-badge" title={`${streak}-day focus streak, finish a sprint each day to keep it going`}>
          🔥 {streak}
        </span>
      )}
      {secondsLeft > 0 ? (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-100 text-xs font-bold tabular-nums" data-testid="focus-timer-countdown">
          <Timer size={13} /> {fmt(secondsLeft)}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <Timer size={13} className="text-white/50" />
          {savedSprint > 0 && (
            <button type="button" onClick={resumeTimer} data-testid="focus-timer-resume"
              title="Resume your unfinished focus sprint"
              className="px-2 py-1 rounded-full bg-emerald-500/25 hover:bg-emerald-500/40 text-emerald-100 text-[11px] font-bold inline-flex items-center gap-0.5">
              <Play size={9} /> Resume {fmt(savedSprint)}
            </button>
          )}
          {TIMER_MINUTES.map((m) => (
            <button key={m} type="button" onClick={() => startTimer(m)} data-testid={`focus-timer-${m}`}
              className="px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-[11px] font-bold inline-flex items-center gap-0.5">
              <Play size={9} /> {m}m
            </button>
          ))}
        </span>
      )}
      <button type="button" onClick={toggle} data-testid="focus-exit-btn"
        title="Exit focus mode"
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold">
        <Minimize2 size={13} /> Exit
      </button>
    </div>
  );
}

// A compact "question 3 of 5" progress ring, shown in Focus Mode where the
// header's usual progress bar is hidden, so kids still see how far they've come.
function QuizProgressRing({ current, total }) {
  const done = Math.min(total, (current || 0) + 1); // 0-indexed → human "3 of 5"
  const r = 11;
  const c = 2 * Math.PI * r;
  const frac = total > 0 ? Math.min(1, done / total) : 0;
  return (
    <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full bg-white/5" data-testid="focus-progress-ring" title={`Question ${done} of ${total}`}>
      <svg width="28" height="28" viewBox="0 0 28 28" className="-rotate-90">
        <circle cx="14" cy="14" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
        <circle cx="14" cy="14" r={r} fill="none" stroke="#818cf8" strokeWidth="3" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - frac)} style={{ transition: "stroke-dashoffset 300ms ease" }} />
      </svg>
      <span className="text-white/80 text-[11px] font-bold tabular-nums">{done} of {total}</span>
    </span>
  );
}
